import { BreakPoint, getDevice } from '@adornis/base/client/frontend-helpers/window-size.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/dragdrop/x-drag-drop.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { CampusLessonDiagnostic, LearningDataAPI } from '@chancenwerk/campus-learning-content/api/LearningDataAPI.js';
import { CampusLesson } from '@chancenwerk/campus-learning-content/entities/CampusLesson.js';
import { CampusArea, CampusSection } from '@chancenwerk/campus-learning-content/entities/CampusSection.js';
import '@chancenwerk/components/chancen-component-card-title.js';
import { CampusUserGroup } from '@chancenwerk/db/CampusUserGroup.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import { StudentAPI } from '@chancenwerk/master-data-management/users/api/StudentAPI.js';
import { type ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { AnimateController, animate, fadeIn } from '@lit-labs/motion';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { combineLatest, filter, switchMap } from 'rxjs';
import { CampusRoutes } from '../../routes.js';
import { CampusDialogEditSection } from '../dialogs/campus-dialog-edit-section.js';
import './campus-component-lesson-card.js';

@customElement('campus-component-section-card')
export class CampusComponentSectionCard extends ChemistryLitElement {
  @property({ attribute: false }) public mode: 'play' | 'edit' | 'custom' = 'play';
  @property({ attribute: false }) public area: CampusArea = CampusArea.STUDENTS;
  @property({ attribute: false }) public showOnlyAssigned = new RXController(this, false);
  @property({ attribute: false }) public showOnlyMarked = new RXController(this, false);
  @property({ attribute: false }) public showOnlyStarted = new RXController(this, false);
  @property({ attribute: false }) public userID = new RXController<Maybe<string>>(this, null);

  @property({ type: Boolean }) condensed = false;
  @property({ type: Boolean }) dashboard = false;

  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());

  @property({ attribute: false }) public sectionID = new RXController<string | null>(this, null);

  @state() private readonly _section = new RXController(
    this,
    this.sectionID.observable.pipe(
      switchMap(async id => (id ? CampusSection.getByID<CampusSection>(id)(CampusSection.allFields) : null)),
    ),
  );

  @state() private readonly _userGroups = new RXController(
    this,
    combineLatest([this._currentUser.observable, this.userID.observable]).pipe(
      switchMap(u =>
        CampusUserGroup.subscribeAllGroupsForUserID(u[1] ?? u[0]?._id ?? '')({
          _class: 1,
          _id: 1,
          assignedLessonsIDs: 1,
        }),
      ),
    ),
  );

  @property({ attribute: false }) public userSearchText = new RXController<string>(this, '');
  @property({ attribute: false }) public userHiddenFromStudents = new RXController<Maybe<boolean>>(this, null);

  @state() private readonly _lessonDiagnostic = new RXController(
    this,
    combineLatest([this._currentUser.observable, this.userID.observable, this._section.observable]).pipe(
      filter(Boolean),
      switchMap(([user, userID, section]) =>
        this.mode === 'play'
          ? (LearningDataAPI.getDiagnosticForLessons(
              userID || user?._id || 'NOT_LOGGED_IN',
              section?.lessonIDs ?? [],
            )(CampusLessonDiagnostic.allFields) as Promise<CampusLessonDiagnostic[]>)
          : (Promise.resolve([]) as Promise<CampusLessonDiagnostic[]>),
      ),
    ),
  );

  @state() public markedLessonIDs = new RXController<string[]>(this, [] as string[]);
  @state() public hideLessonsIDs = new RXController<string[]>(this, []);

  @state() private readonly _animationController = new AnimateController(this, {});

  private _shouldHide() {
    if (this.showOnlyStarted.value) {
      const lessons = this._filterLessons(this._section.value?.lessons ?? []);
      if (lessons.length === 0) {
        return true;
      }
    }
    return false;
  }

  override render() {
    if (this._shouldHide()) {
      return nothing;
    }

    return html`
      <cr-card
        ${css({
          width: '100%',
          height: '100%',
          backgroundColor: this['dark-cards'] ? this.colors.neutral.shade(50) : 'white',
          marginBottom: this.area !== CampusArea.STUDENTS ? this.spacing.md : '',
        })}
        wrap
      >
        ${this._section.isLoading ? this._loadingState() : this._content()}
      </cr-card>
    `;
  }

  private _loadingState() {
    return html`
      <cr-flex space="md" w100 crossaxis-center center ${css({ minHeight: '500px' })}>
        <cr-campus-loader></cr-campus-loader>
      </cr-flex>
    `;
  }

  private _sectionInSavedLessons() {
    if (!this._currentUser.value || !this.sectionID.value) return;

    return this._currentUser.value.savedCampusSectionIDs?.includes(this.sectionID.value);
  }

  private _content() {
    if (!this._section.value) return 'leer';

    if (this.condensed) {
      return html`
        <cr-flex space="md">
          <chancen-component-card-title> ${this._section.value.name} </chancen-component-card-title>

          ${!this.dashboard
            ? html` <cr-flex horizontal space="md">
                <cr-text>
                  ${this._section.value.subjects.length < 2 ? 'Fach' : 'Fächer'}:
                  ${this._section.value.subjects.join(', ')}
                </cr-text>
                <cr-text ${css({ display: 'none' })}>
                  ${this._section.value.forGrades.length < 2 ? 'Jahrgangstufe' : 'Jahrgangstufen'}:
                  ${this._section.value.forGrades.join(', ')}
                </cr-text>
              </cr-flex>`
            : nothing}
          ${this._section.isLoading
            ? html`
                <cr-flex center crossaxis-center flex>
                  <cr-campus-loader></cr-campus-loader>
                </cr-flex>
              `
            : this._section.value.lessons?.length
            ? this._getDragDrop()
            : html`<cr-text>In diesem Bereich sind keine Lektionen hinterlegt</cr-text>`}
        </cr-flex>
      `;
    }

    return html`
      <cr-flex space="md" w100>
        <cr-flex horizontal space-between ${css({ marginLeft: this.spacing.sm, marginTop: this.spacing.sm })}>
          <cr-flex
            horizontal
            crossaxis-center
            space="sm"
            w100
            @click=${async () => {
              switch (this.mode) {
                case 'edit':
                  await CampusDialogEditSection.showPopup({
                    draggable: getDevice() <= BreakPoint.tablet,
                    modal: true,
                    props: { section: this._section.value },
                  });
                  this.requestUpdate();
                  break;
                case 'play':
                  break;
                case 'custom':
                  this.dispatchEvent(
                    new CustomEvent('sectionClicked', {
                      detail: {
                        section: this._section.value,
                      },
                    }),
                  );
                  break;
                default:
                  break;
              }
            }}
            ${this.mode === 'edit' ? css({ marginTop: 'md', cursor: 'pointer' }) : css({ marginTop: 'md' })}
          >
            <cr-h2> ${this._section.value.name} </cr-h2>
            ${this._section.value.hiddenFromStudents
              ? html` <cr-icon ${css({ fontSize: '2rem', color: '#adadad' })}> visibility_off </cr-icon> `
              : nothing}
          </cr-flex>
          ${this.mode === 'play'
            ? html`
                <cr-button
                  mode="fab"
                  icon=${this._sectionInSavedLessons() ? 'bookmark_added' : 'bookmark_add'}
                  tone=${this._sectionInSavedLessons() ? 'checked' : ''}
                  @click=${async () => {
                    // Check if array exists
                    if (!this._currentUser.value || !this.sectionID.value) return;
                    if (!this._currentUser.value.savedCampusSectionIDs)
                      this._currentUser.value.savedCampusSectionIDs = [];

                    // adding or removing the element from the saved lessons list
                    const index = this._currentUser.value.savedCampusSectionIDs.findIndex(
                      sID => this.sectionID.value === sID,
                    );
                    if (index === -1) {
                      this._currentUser.value.savedCampusSectionIDs.push(this.sectionID.value);
                    } else {
                      this._currentUser.value.savedCampusSectionIDs.splice(index, 1);
                    }

                    await StudentAPI.setSavedCampusSections(this._currentUser.value.savedCampusSectionIDs)();
                    this.requestUpdate();
                  }}
                >
                  <cr-tooltip>
                    <cr-text ${css({ maxWidth: '400px', padding: `0 ${this.spacing.sm}` })}>
                      Themenbereich zu deiner Merkliste hinzufügen.
                    </cr-text>
                  </cr-tooltip>
                </cr-button>
              `
            : nothing}
        </cr-flex>
        ${this.area === CampusArea.STUDENTS
          ? html` <cr-flex horizontal space="md" ${css({ marginLeft: this.spacing.sm })}>
              <cr-text>
                ${this._section.value.subjects.length < 2 ? 'Fach' : 'Fächer'}:
                ${this._section.value.subjects.join(', ')}
              </cr-text>
              ${this.mode === 'play'
                ? nothing
                : html`
                    <cr-text>
                      ${this._section.value.forGrades.length < 2 ? 'Jahrgangstufe' : 'Jahrgangstufen'}:
                      ${this._section.value.forGrades.join(', ')}
                    </cr-text>
                  `}
            </cr-flex>`
          : nothing}
        <cr-flex ${css({ borderBottom: '1px solid rgba(0,0,0,0.1)' })}></cr-flex>
        ${this._section.isLoading
          ? html`
              <cr-flex center crossaxis-center flex>
                <cr-campus-loader></cr-campus-loader>
              </cr-flex>
            `
          : this._getDragDrop()}
      </cr-flex>
    `;
  }

  private _getDragDrop() {
    const lessons = this._filterLessons(this._section.value?.lessons ?? []);
    return html`
      ${this._section.value?.lessons?.length
        ? html`<x-drag-drop
            ${css({ display: 'flex' })}
            degrees-on-move=${0}
            drag-mode=${'column'}
            child-selector=${'.draggable'}
            @resorted=${async (e: CustomEvent) => {
              if (!this._section.value) return;
              this._section.value.lessonIDs = e.detail.order;

              // Sort the client lessons to have an instant UI update
              const newLessonOrder: CampusLesson[] = [];
              e.detail.order.forEach(lessonID => {
                const lesson = this._section.value?.lessons?.find(l => l._id === lessonID);
                if (!lesson) {
                  console.warn('Something went wrong while sorting, this should not happen!');
                  return;
                }
                newLessonOrder.push(lesson);
              });
              this._section.value.lessons = newLessonOrder;

              await this._section.value.save();
              this.requestUpdate();
              void ChromiumSnackbar.show('Sortierung wurde gespeichert');
            }}
          >
            <cr-flex space="sm" space-between w100>
              ${repeat(
                this.condensed && !this.dashboard ? lessons.slice(0, 3) : lessons,
                lesson => lesson._id,
                (lesson, index) => {
                  return html`<campus-component-lesson-card
                    ${animate({
                      keyframeOptions: {
                        duration: 500,
                        delay: (index * 500) / lessons.length,
                        fill: 'both',
                      },
                      disabled: this._animationController.disabled,
                      in: fadeIn,
                      onComplete: animate => {
                        // Stop all animations rendering
                        this._animationController.disabled = true;
                      },
                    })}
                    ?condensed=${this.condensed}
                    key=${lesson._id}
                    class="draggable"
                    .lesson=${lesson}
                    .section=${this._section.value}
                    .mode=${!this.dashboard
                      ? this.mode
                      : this.area === CampusArea.STUDENTS
                      ? 'progress'
                      : 'progress_employee'}
                    .marked=${this.markedLessonIDs.value.includes(lesson._id)}
                    .diagnosticsForLessons=${this._lessonDiagnostic.value ?? []}
                    @lessonClicked=${async e => {
                      const event = new CustomEvent('lessonClicked', {
                        detail: {
                          lesson: e.detail.lesson,
                          section: e.detail.section,
                        },
                      });
                      this.dispatchEvent(event);
                    }}
                  ></campus-component-lesson-card>`;
                },
              )}
              ${this.condensed && !this.dashboard
                ? html`
                    <cr-text>
                      ${lessons.length - 3 > 1
                        ? `Und ${lessons.length - 3} weitere Lektionen...`
                        : lessons.length - 3 == 1
                        ? 'Und eine weitere Lektion...'
                        : ''}
                    </cr-text>
                  `
                : nothing}
            </cr-flex>
          </x-drag-drop>`
        : html` <cr-text ${css({ padding: this.spacing.md })}>
            In diesem Bereich sind keine Lektionen hinterlegt
          </cr-text>`}
      ${this.mode === 'edit'
        ? html`<cr-flex
            ${css({
              alignItems: 'end',
              borderTop: '1px solid rgba(0,0,0,0.1)',
              paddingTop: this.spacing.md,
              paddingBottom: this.spacing.sm,
            })}
          >
            <cr-button
              icon="add"
              href=${CampusRoutes.lessons + `/edit?area=${CampusArea.STUDENTS}&section=${this._section.value?._id}`}
              target="_blank"
              ${css({ maxWidth: '200px' })}
            >
              Lektion
            </cr-button>
          </cr-flex>`
        : nothing}
    `;
  }

  private _filterLessons(localLessons: CampusLesson[]): CampusLesson[] {
    if (this.showOnlyMarked.value) {
      localLessons = localLessons.filter(lesson => this.markedLessonIDs.value.includes(lesson._id));
    }

    if (this.showOnlyStarted.value) {
      localLessons = localLessons.filter(lesson => {
        const diagnostic = this._lessonDiagnostic.value?.find(d => d.lessonID === lesson._id);
        return (diagnostic?.ratio ?? 0) > 0.0;
      });
    }

    // Textfilter for searchbar
    if ((this.userSearchText.value || '').length) {
      localLessons = this._section.value?.name
        .toLocaleLowerCase()
        .includes(this.userSearchText.value.toLocaleLowerCase())
        ? localLessons
        : localLessons.filter(lesson =>
            lesson.name.toLocaleLowerCase().includes(this.userSearchText.value.toLocaleLowerCase()),
          );
    }

    // Filter lessons if they are hidden from students
    if (this.mode === 'play') {
      localLessons = localLessons.filter(
        lesson => !lesson.hiddenFromStudents && !this._section.value?.hiddenFromStudents,
      );
    }

    // Filter only assigned lessons
    if (this.showOnlyAssigned.value)
      localLessons = localLessons.filter(
        lesson =>
          this._userGroups.value?.some(group => group.assignedLessonsIDs?.includes(lesson._id)) ||
          this._currentUser.value?.savedCampusSectionIDs?.includes(this.sectionID.value ?? ''),
      );

    if (typeof this.userHiddenFromStudents.value === 'boolean') {
      localLessons = localLessons.filter(lesson => {
        return lesson.hiddenFromStudents === this.userHiddenFromStudents.value;
      });
    }

    localLessons = localLessons.filter(lesson => !this.hideLessonsIDs.value.includes(lesson._id));
    return localLessons;
  }
}
